/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CheckCircle2, Gavel, Home, Monitor, Search, Star, WifiOff } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { MicButton } from './Agent';

/** M26 Мобильное приложение сотрудника (/m): задачи и заседания дня, режим «в суде», избранные дела без сети, съёмка документов, диктовка */
const FAV = 'kd_m_fav';
type Fav = { id: string; title: string; savedAt: string; matter: any; cases: any[]; tasks: any[] };
const favs = { get: (): Fav[] => { try { return JSON.parse(localStorage.getItem(FAV) ?? '[]'); } catch { return []; } }, set: (v: Fav[]) => { try { localStorage.setItem(FAV, JSON.stringify(v)); } catch { /* нет места */ } } };
const t = (s: string) => new Date(s).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
const d = (s: string) => new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const isToday = (s: string) => new Date(s).toDateString() === new Date().toDateString();

function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => { const a = () => setOn(true); const b = () => setOn(false); addEventListener('online', a); addEventListener('offline', b); return () => { removeEventListener('online', a); removeEventListener('offline', b); }; }, []);
  return on;
}

export function StaffMobilePage() {
  const brand = useBranding();
  const { me } = useAuth();
  const online = useOnline();
  const [tab, setTab] = useState<'today' | 'court' | 'matters' | 'scan'>('today');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    document.title = brand.shortName;
    let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
    if (!link) { link = Object.assign(document.createElement('link'), { rel: 'manifest' }); document.head.appendChild(link); }
    link.href = '/api/v1/public/manifest/staff';
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  }, [brand.shortName]);
  useNotifier(online);
  const tabs: [typeof tab, string, ReactNode][] = [['today', 'Сегодня', <Home key="h" size={20} />], ['court', 'В суде', <Gavel key="g" size={20} />], ['matters', 'Дела', <Star key="s" size={20} />], ['scan', 'Съёмка', <Camera key="c" size={20} />]];
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="sticky top-0 z-10 flex items-center justify-between bg-brand-600 px-4 py-3 text-white">
        <span className="flex items-center gap-2">{brand.logo && <img src={brand.logo} alt="" className="h-7 w-7 rounded bg-white object-contain" />}<b>{brand.shortName}</b></span>
        <span className="flex items-center gap-3 text-xs">{!online && <span className="flex items-center gap-1"><WifiOff size={14} />нет сети</span>}{me?.fullName.split(' ')[0]}<a href="/" className="opacity-80" aria-label="Полная версия"><Monitor size={18} /></a></span>
      </header>
      <main className="mx-auto w-full max-w-xl flex-1 p-4 pb-24">
        {open ? <MatterView id={open} online={online} onBack={() => setOpen(null)} />
          : tab === 'today' ? <Today online={online} onOpen={setOpen} />
          : tab === 'court' ? <Court online={online} onOpen={setOpen} />
          : tab === 'matters' ? <Matters online={online} onOpen={setOpen} />
          : <Scan online={online} />}
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-4 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)]">
        {tabs.map(([id, l, icon]) => <button key={id} onClick={() => { setTab(id); setOpen(null); }} className={`flex flex-col items-center gap-0.5 py-2 text-[11px] ${tab === id && !open ? 'text-brand-700' : 'text-slate-500'}`}>{icon}{l}</button>)}
      </nav>
    </div>
  );
}

/** Уведомления на устройстве без внешних push-сервисов: пока приложение открыто, раз в 5 минут проверяются заседания и просрочки */
function useNotifier(online: boolean) {
  useEffect(() => {
    if (!online || !('Notification' in window)) return;
    const seen = new Set<string>();
    const tick = async () => {
      if (Notification.permission !== 'granted') return;
      try {
        const h = await api.get<any[]>('/court-cases/hearings');
        const reg = await navigator.serviceWorker?.getRegistration();
        for (const x of h) {
          const mins = (new Date(x.at).getTime() - Date.now()) / 60_000;
          const key = `h${x.id}${x.at}`;
          if (mins > 0 && mins <= 90 && !seen.has(key)) {
            seen.add(key);
            const body = `${t(x.at)}, ${x.court}${x.place ? `, ${x.place}` : ''}`;
            if (reg) reg.showNotification(`Заседание ${x.number}`, { body, data: { url: '/m' }, tag: key }); else new Notification(`Заседание ${x.number}`, { body });
          }
        }
      } catch { /* нет связи */ }
    };
    void tick();
    const i = setInterval(tick, 5 * 60_000);
    return () => clearInterval(i);
  }, [online]);
}

function Card({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return <div onClick={onClick} className={`rounded-lg bg-white p-4 ${onClick ? 'cursor-pointer active:bg-slate-50' : ''}`}>{children}</div>;
}

function Today({ online, onOpen }: { online: boolean; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const { data: tasks } = useQuery({ queryKey: ['m-tasks'], queryFn: () => api.get<any>('/tasks?scope=mine&status=TODO,IN_PROGRESS&pageSize=100'), enabled: online });
  const { data: h } = useQuery({ queryKey: ['m-hearings'], queryFn: () => api.get<any[]>('/court-cases/hearings'), enabled: online });
  const done = useMutation({ mutationFn: (id: string) => api.patch(`/tasks/${id}`, { status: 'DONE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['m-tasks'] }) });
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');
  if (!online) return <Card>Нет сети. Откройте вкладку «Дела»: сохранённые дела доступны без интернета.</Card>;
  const list: any[] = (tasks?.items ?? []).filter((x: any) => !x.dueAt || new Date(x.dueAt) < new Date(Date.now() + 86400_000));
  const today = (h ?? []).filter((x) => isToday(x.at));
  return (
    <div className="space-y-3">
      {perm === 'default' && <Card><div className="text-sm">Включить напоминания о заседаниях на этом устройстве?</div><button className="mt-2 rounded bg-brand-600 px-3 py-1.5 text-sm text-white" onClick={() => Notification.requestPermission().then(setPerm)}>Включить</button></Card>}
      <div className="text-xs font-semibold uppercase text-slate-500">Заседания сегодня ({today.length})</div>
      {today.map((x) => <Card key={x.id} onClick={() => x.matterId && onOpen(x.matterId)}><div className="text-lg font-semibold">{t(x.at)}</div><div>{x.number}</div><div className="text-sm text-slate-500">{x.court}{x.place ? `, ${x.place}` : ''}</div></Card>)}
      {!today.length && <div className="text-sm text-slate-500">Заседаний нет</div>}
      <div className="pt-2 text-xs font-semibold uppercase text-slate-500">Задачи на сегодня и просроченные ({list.length})</div>
      {list.map((x) => (
        <Card key={x.id}><div className="flex items-start justify-between gap-2">
          <div onClick={() => x.matterId && onOpen(x.matterId)}><div className={x.dueAt && new Date(x.dueAt) < new Date() ? 'text-red-700' : ''}>{x.title}</div>{x.dueAt && <div className="text-xs text-slate-500">до {d(x.dueAt)}</div>}</div>
          <button aria-label="Выполнено" onClick={() => done.mutate(x.id)} className="text-slate-400 active:text-green-600"><CheckCircle2 size={24} /></button>
        </div></Card>
      ))}
    </div>
  );
}

/** Режим «в суде»: крупно ближайшие заседания, быстрая заметка голосом в дело */
function Court({ online, onOpen }: { online: boolean; onOpen: (id: string) => void }) {
  const { data: h } = useQuery({ queryKey: ['m-hearings'], queryFn: () => api.get<any[]>('/court-cases/hearings'), enabled: online });
  const saved = favs.get().flatMap((f) => f.cases.filter((c) => c.nextHearingAt && isToday(c.nextHearingAt)).map((c) => ({ id: c.id, number: c.number, court: c.court, at: c.nextHearingAt, place: c.nextHearingPlace, matterId: f.id })));
  const list = online ? (h ?? []).filter((x) => new Date(x.at).getTime() < Date.now() + 2 * 86400_000) : saved;
  return (
    <div className="space-y-3">
      {!online && <div className="text-sm text-slate-500">Без сети показаны заседания из сохранённых дел.</div>}
      {list.map((x) => (
        <div key={x.id} className="rounded-xl bg-white p-5" onClick={() => x.matterId && onOpen(x.matterId)}>
          <div className="text-3xl font-bold">{t(x.at)}</div>
          <div className="text-sm text-slate-500">{isToday(x.at) ? 'сегодня' : new Date(x.at).toLocaleDateString('ru-RU')}</div>
          <div className="mt-2 text-xl font-semibold">{x.number}</div>
          <div>{x.court}</div>
          {x.place && <div className="text-lg">{x.place}</div>}
          {x.partyName && <div className="text-sm text-slate-500">{x.partyName}</div>}
        </div>
      ))}
      {!list.length && <Card>Ближайших заседаний нет</Card>}
    </div>
  );
}

function Matters({ online, onOpen }: { online: boolean; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const { data } = useQuery({ queryKey: ['m-matters', q], queryFn: () => api.get<any>(`/matters?pageSize=30${q ? `&q=${encodeURIComponent(q)}` : ''}`), enabled: online });
  const f = favs.get();
  return (
    <div className="space-y-3">
      {!!f.length && <><div className="text-xs font-semibold uppercase text-slate-500">Сохранены на устройстве</div>
        {f.map((x) => <Card key={x.id} onClick={() => onOpen(x.id)}><div className="flex items-center gap-2"><Star size={14} className="fill-amber-400 text-amber-400" />{x.title}</div><div className="text-xs text-slate-500">сохранено {d(x.savedAt)}</div></Card>)}</>}
      {online && <>
        <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2"><Search size={16} className="text-slate-400" /><input className="flex-1 text-base outline-none" placeholder="Поиск дела" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        {(data?.items ?? []).filter((m: any) => !f.some((x) => x.id === m.id)).map((m: any) => <Card key={m.id} onClick={() => onOpen(m.id)}><div>{m.title}</div><div className="text-xs text-slate-500">{m.clientName}</div></Card>)}
      </>}
      {!online && !f.length && <Card>Нет сети и нет сохранённых дел. Откройте дело при наличии сети и нажмите звёздочку, чтобы оно было доступно без интернета.</Card>}
    </div>
  );
}

function MatterView({ id, online, onBack }: { id: string; online: boolean; onBack: () => void }) {
  const fav = favs.get().find((x) => x.id === id);
  const { data: live } = useQuery({ queryKey: ['m-matter', id], queryFn: async () => ({ matter: await api.get<any>(`/matters/${id}`), cases: await api.get<any[]>(`/court-cases?matterId=${id}`).catch(() => []), tasks: (await api.get<any>(`/tasks?matterId=${id}&status=TODO,IN_PROGRESS&pageSize=50`)).items }), enabled: online });
  const [isFav, setFav] = useState(!!fav);
  const [note, setNote] = useState('');
  const [ok, setOk] = useState(false);
  const data = live ?? fav;
  useEffect(() => { if (live && isFav) save(live); }, [live]);
  function save(x: any) { favs.set([{ id, title: x.matter.title, savedAt: new Date().toISOString(), matter: x.matter, cases: x.cases, tasks: x.tasks }, ...favs.get().filter((f) => f.id !== id)].slice(0, 30)); }
  const toggle = () => { if (isFav) { favs.set(favs.get().filter((f) => f.id !== id)); setFav(false); } else if (live) { save(live); setFav(true); } };
  const add = useMutation({ mutationFn: () => api.post(`/matters/${id}/timeline`, { kind: 'NOTE', title: 'Заметка с телефона', body: note }), onSuccess: () => { setNote(''); setOk(true); } });
  if (!data) return <Card>{online ? 'Загрузка...' : 'Дело не сохранено на устройстве'}</Card>;
  const m = data.matter;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between"><button className="text-sm text-slate-500" onClick={onBack}>Назад</button>
        <button onClick={toggle} disabled={!online} className="flex items-center gap-1 text-sm"><Star size={18} className={isFav ? 'fill-amber-400 text-amber-400' : 'text-slate-400'} />{isFav ? 'на устройстве' : 'сохранить без сети'}</button></div>
      <Card><div className="text-lg font-semibold">{m.title}</div><div className="text-sm text-slate-500">{m.number} · {m.clientName ?? m.client?.name ?? ''}</div>{m.description && <div className="mt-2 whitespace-pre-wrap text-sm">{m.description}</div>}</Card>
      {data.cases.map((c: any) => <Card key={c.id}><div className="font-medium">{c.number}</div><div className="text-sm">{c.court}{c.judge ? `, судья ${c.judge}` : ''}</div>{c.nextHearingAt && <div className="text-sm">Заседание: {d(c.nextHearingAt)}{c.nextHearingPlace ? `, ${c.nextHearingPlace}` : ''}</div>}</Card>)}
      {!!data.tasks.length && <Card><div className="mb-1 text-xs font-semibold uppercase text-slate-500">Открытые задачи</div><ul className="space-y-1 text-sm">{data.tasks.map((x: any) => <li key={x.id}>{x.title}{x.dueAt ? `, до ${d(x.dueAt)}` : ''}</li>)}</ul></Card>}
      {online && <Card>
        <div className="mb-2 text-sm font-medium">Заметка в дело</div>
        <textarea rows={3} className="w-full rounded border border-slate-300 p-2 text-base" value={note} onChange={(e) => { setNote(e.target.value); setOk(false); }} placeholder="Например: заседание отложено на 15.11, суд запросил выписку" />
        <div className="mt-2 flex items-center gap-2"><button className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white disabled:opacity-50" disabled={!note.trim() || add.isPending} onClick={() => add.mutate()}>Сохранить</button><MicButton onText={(x) => setNote(note ? `${note} ${x}` : x)} />{ok && <span className="text-sm text-green-700">Добавлено</span>}</div>
      </Card>}
      {!online && <div className="text-xs text-slate-500">Данные сохранены {fav && d(fav.savedAt)}. Изменения доступны при подключении к сети.</div>}
    </div>
  );
}

/** Съёмка документа камерой телефона: фото загружается в документы и распознаётся (M29) */
function Scan({ online }: { online: boolean }) {
  const [matterId, setMatterId] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [done, setDone] = useState<string[]>([]);
  const f = favs.get();
  const { data } = useQuery({ queryKey: ['m-matters', ''], queryFn: () => api.get<any>('/matters?pageSize=30'), enabled: online });
  const up = useMutation({
    mutationFn: async () => {
      const ids: string[] = [];
      for (const [i, file] of files.entries()) {
        const fd = new FormData(); fd.append('file', file); fd.append('title', title ? (files.length > 1 ? `${title}, стр. ${i + 1}` : title) : file.name); fd.append('kind', 'Скан'); if (matterId) fd.append('matterId', matterId);
        ids.push((await api.post<any>('/documents', fd)).id);
      }
      return ids;
    },
    onSuccess: (ids) => { setDone(ids); setFiles([]); setTitle(''); },
  });
  if (!online) return <Card>Съёмка и загрузка доступны при подключении к сети.</Card>;
  const opts = [...f.map((x) => ({ id: x.id, title: x.title })), ...(data?.items ?? []).filter((m: any) => !f.some((x) => x.id === m.id))];
  return (
    <div className="space-y-3">
      <Card>
        <label className="block text-sm">Дело<select className="mt-1 w-full rounded border border-slate-300 p-2 text-base" value={matterId} onChange={(e) => setMatterId(e.target.value)}><option value="">без дела</option>{opts.map((m: any) => <option key={m.id} value={m.id}>{m.title}</option>)}</select></label>
        <label className="mt-2 block text-sm">Название<input className="mt-1 w-full rounded border border-slate-300 p-2 text-base" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="например, Определение суда" /></label>
        <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-brand-300 p-6 text-brand-700"><Camera size={22} />Сфотографировать страницу<input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && setFiles([...files, e.target.files[0]])} /></label>
        {!!files.length && <div className="mt-2 text-sm">Страниц: {files.length}</div>}
        {up.error && <div className="mt-2 text-sm text-red-600">{(up.error as Error).message}</div>}
        <button className="mt-3 w-full rounded-lg bg-brand-600 py-2.5 text-white disabled:opacity-50" disabled={!files.length || up.isPending} onClick={() => up.mutate()}>{up.isPending ? 'Загружаю...' : 'Загрузить'}</button>
      </Card>
      {!!done.length && <Card>Загружено: {done.length}. Текст будет распознан автоматически.</Card>}
    </div>
  );
}
